import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/common/Footer';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-foreground mb-2">Cookie Policy</h1>
        <p className="text-sm text-muted-foreground mb-2">Last updated: April 2026</p>
        <p className="text-sm text-muted-foreground mb-10">
          This Cookie Policy explains what cookies and similar technologies CTX uses, why we use
          them, and how you can manage your preferences.
        </p>

        <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. What Are Cookies</h2>
            <p>
              Cookies are small text files placed on your device when you visit a website or use a
              web application. They allow the service to remember information about your visit,
              such as whether you are logged in. Similar technologies include local storage and
              session storage, which we also use for authentication purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Cookies & Storage We Use</h2>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-left border border-border rounded-lg text-xs">
                <thead>
                  <tr className="border-b border-border bg-white/5">
                    <th className="px-4 py-3 text-foreground font-medium">Name / Type</th>
                    <th className="px-4 py-3 text-foreground font-medium">Category</th>
                    <th className="px-4 py-3 text-foreground font-medium">Purpose</th>
                    <th className="px-4 py-3 text-foreground font-medium">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3">Auth access token (localStorage)</td>
                    <td className="px-4 py-3">Essential</td>
                    <td className="px-4 py-3">Keeps you authenticated between page loads</td>
                    <td className="px-4 py-3">Session / until logout</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3">Auth refresh token (localStorage)</td>
                    <td className="px-4 py-3">Essential</td>
                    <td className="px-4 py-3">Silently refreshes your session so you stay logged in</td>
                    <td className="px-4 py-3">Up to 7 days</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3">ctx-cookie-consent (localStorage)</td>
                    <td className="px-4 py-3">Essential</td>
                    <td className="px-4 py-3">Stores your cookie consent preference so we don't show the banner repeatedly</td>
                    <td className="px-4 py-3">12 months</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3">Analytics cookies / identifiers</td>
                    <td className="px-4 py-3">Analytics (optional)</td>
                    <td className="px-4 py-3">Help us understand how the app is used (page views, feature interactions) so we can improve the Service</td>
                    <td className="px-4 py-3">Up to 12 months</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Payment-related cookies</td>
                    <td className="px-4 py-3">Functional</td>
                    <td className="px-4 py-3">Set by our payment processor to manage secure checkout sessions. Governed by the payment processor's own cookie policy.</td>
                    <td className="px-4 py-3">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. Essential Cookies & Storage</h2>
            <p>
              Essential cookies and local storage entries are strictly necessary for the Service to
              function. Without them you cannot log in, stay authenticated, or use any features of
              CTX. These cannot be disabled without preventing you from using the platform.
            </p>
            <p className="mt-3">
              Under most privacy regulations, essential cookies do not require your prior consent
              because they are strictly necessary to provide the service you have requested.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. Analytics Cookies</h2>
            <p>
              We use an analytics service to collect anonymised data about how users interact with
              CTX — which features are used, how users navigate the app, and where users
              encounter issues. This helps us prioritise improvements and fix bugs.
            </p>
            <p className="mt-3">
              Analytics cookies are only set if you accept them via our cookie consent banner.
              The analytics data collected is:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Tied to an anonymised identifier — not directly to your name or email.</li>
              <li>Not used for advertising or profiling.</li>
              <li>Not sold to any third party.</li>
              <li>Used exclusively to improve CTX.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. Managing Your Preferences</h2>
            <p>
              You can control cookies and tracking in the following ways:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong className="text-foreground">Cookie banner:</strong> When you first visit
                CTX, you are presented with a cookie consent banner. You can accept or decline
                optional analytics cookies at that time.
              </li>
              <li>
                <strong className="text-foreground">Change your preference:</strong> You can
                withdraw or update your consent at any time by clearing the{' '}
                <code className="bg-white/10 px-1 rounded text-xs">ctx-cookie-consent</code> key
                from your browser's local storage and refreshing the page, which will trigger the
                consent banner again.
              </li>
              <li>
                <strong className="text-foreground">Browser settings:</strong> You can configure
                your browser to block or delete cookies. Note that blocking essential cookies or
                local storage will prevent you from using CTX, as authentication depends on them.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. Third-Party Cookies</h2>
            <p>
              Our payment processor may set its own cookies during the checkout flow to manage
              secure payment sessions. These cookies are set directly by the payment processor and
              are governed by their own privacy and cookie policies, which we encourage you to
              review.
            </p>
            <p className="mt-3">
              We do not permit any advertising networks, data brokers, or social media platforms
              to set cookies on CTX.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">7. Changes to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time as we introduce new features or
              in response to regulatory changes. Material changes will be notified via the app or
              by email. Continued use of the Service after the effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. More Information</h2>
            <p>
              For full details on how we handle your personal data, see our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              For questions about cookies, contact us at{' '}
              <span className="text-foreground">support@ctxtraders.com</span>.
            </p>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
}
